type ActionType = 'start' | 'action' | 'stop'
type Action = () => unknown

type Act = {
  type: ActionType
  action: Action
  silent?: boolean
}

export class SyncController {
  private started = false
  private promise: unknown
  private acts: Act[] = []

  start(action: Action) {
    this.acts.push({ type: 'start', action })
    this.process()
  }

  stop(action: Action) {
    this.acts.push({ type: 'stop', action })
    this.process()
  }

  action(action: Action, silent?: boolean) {
    this.acts.push({ type: 'action', action, silent })
    this.process()
  }

  get forceShow(): boolean {
    if (this.acts.find(act => ['start', 'stop'].includes(act.type))) return false
    return !!this.acts.find(act => act.type === 'action' && !act.silent)
  }

  private process() {
    setTimeout(() => void this._process())
  }

  private async _process() {
    if (this.promise) return

    this.shift()
    const act = this.acts.splice(0, 1)[0]
    if (!act) return

    const { type, action } = act
    if (type === 'start') {
      this.promise = this._start(action)
    } else if (type === 'stop') {
      this.promise = this._stop(action)
    } else if (type === 'action') {
      this.promise = this._action(action)
    } else {
      console.error('unknown act', type)
      this.process()
      return
    }

    try {
      await this.promise
    } catch (e) {
      console.error(`action failed`, type, e)
    } finally {
      this.promise = undefined
      this.process()
    }
  }

  private shift() {
    const allowed = this.started ? 'stop' : 'start'
    const cut = this.acts.findLastIndex(act => act.type === allowed)
    if (cut > 0) this.acts.splice(0, cut)
  }

  private async _start(action: Action) {
    if (this.started) throw new Error('already started')
    await action()
    this.started = true
  }

  private async _stop(action: Action) {
    if (!this.started) return
    await action()
    this.started = false
  }

  private async _action(action: Action) {
    if (!this.started) return
    await action()
  }
}
